/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {FormHelperText, FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {MagicWandLine} from '@dropbox/dig-icons/assets';
import {suggestDarkColor, suggestLightColor} from 'views/home/bannerColors';

import {BannerColorInput} from './BannerColorInput';
import styles from './HomeBanners.module.css';

export type BannerColors = {light_background_color: string; dark_background_color: string};

export const BannerColorsField = ({
  colors,
  palette,
  darkPalette,
  onChange,
}: {
  colors: BannerColors;
  /** Colors from the light-mode image, most prominent first. */
  palette: string[];
  /** Colors from the dark-mode image, if there is one. */
  darkPalette: string[];
  onChange: (colors: BannerColors) => void;
}) => (
  <Stack gap="16">
    {palette.length > 0 && (
      <FormRow>
        <FormLabel id="banner-palette-label">Colors from the image</FormLabel>
        <Split gap="8" alignY="center" role="group" aria-labelledby="banner-palette-label">
          {palette.map((color) => (
            <Split.Item key={color}>
              <button
                type="button"
                className={styles.swatch}
                style={{background: color}}
                aria-label={`Build the colors around ${color}`}
                title={color}
                onClick={() =>
                  onChange({
                    light_background_color: suggestLightColor(color),
                    dark_background_color: suggestDarkColor(darkPalette[0] ?? color),
                  })
                }
              />
            </Split.Item>
          ))}
          <Split.Item>
            <Button
              variant="transparent"
              size="small"
              withIconStart={<UIIcon src={MagicWandLine} />}
              onClick={() =>
                onChange({
                  light_background_color: suggestLightColor(palette[0]),
                  dark_background_color: suggestDarkColor(darkPalette[0] ?? palette[0]),
                })
              }
            >
              Match to image
            </Button>
          </Split.Item>
        </Split>
        <FormHelperText>
          Pick a swatch to build both colors around it. The suggestions keep its hue and adjust it
          so the text stays readable.
        </FormHelperText>
      </FormRow>
    )}

    <Split gap="24">
      <Split.Item>
        <BannerColorInput
          id="banner-light-color"
          label="Light mode color"
          value={colors.light_background_color}
          textMode="bright"
          onChange={(light_background_color) => onChange({...colors, light_background_color})}
        />
      </Split.Item>
      <Split.Item>
        <BannerColorInput
          id="banner-dark-color"
          label="Dark mode color"
          value={colors.dark_background_color}
          textMode="dark"
          helperText={
            colors.dark_background_color ? undefined : 'None: dark mode shows the light look.'
          }
          onChange={(dark_background_color) => onChange({...colors, dark_background_color})}
          onClear={() => onChange({...colors, dark_background_color: ''})}
        />
      </Split.Item>
    </Split>
  </Stack>
);
